import { Run, Task, TaskAutomaticallyCancelled, WayPoint } from '@bringg/types';
import { getEnv } from 'mobx-easy';
import { GROUP_VALUES, TaskGeneralResponse, TaskGroupTypes } from '@bringg/dashboard-sdk/dist/Task/Tasks.consts';
import { IReactionDisposer, reaction } from 'mobx';
import { DataOptions, ItemFields, MappedStorableItem } from '@bringg/dashboard-sdk/dist/data-entity';

import { RootEnv } from '../root-env';
import { DEFAULT_PLANNED_TASK_FIELDS } from '../other/consts';
import { emitAnyTaskRunUpdated } from './emit-task-run-updated';
import { getSharedRootStore } from '../root-store';

// This is an ugly hack to remove the usage of alerts view store in here
export const TASK_RUN_UPDATED_GLOBAL_EVENT = 'task-run-updated';

export default class TasksStoreAdapter {
	private readonly api = getEnv<RootEnv>().dashboardSdk.sdk.v2.tasks;
	private dispose: IReactionDisposer;

	public afterRootInit() {
		this.dispose = reaction(
			() => getSharedRootStore().views.authStore.isLoggedIn,
			() => {
				this.api.updateStoreOnEvents();

				this.api.onUpdate(async ({ current, diff }: MappedStorableItem<Task>) => {
					await getSharedRootStore().data.taskInventoriesStore.getTaskInventoriesRejectDetails(current.id);

					if (diff?.run_id) {
						emitAnyTaskRunUpdated();
					}
				});
			}
		);
	}

	public resetAfterRemoveRoot() {
		this.dispose?.();
		this.dispose = undefined;
	}

	public onUpdate(callback: (mappedStorableItem: MappedStorableItem<Task>) => void) {
		return this.api.onUpdate(callback);
	}

	public onCreate(callback: (mappedStorableItem: MappedStorableItem<Task>) => void) {
		return this.api.onCreate(callback);
	}

	public onDelete(callback: (mappedStorableItem: MappedStorableItem<Task>) => void) {
		return this.api.onDelete(callback);
	}

	public onWayPointUpdate(callback: (wayPoint: WayPoint) => void) {
		return this.api.onWayPointUpdate(callback);
	}

	public onWayPointDelete(callback) {
		return this.api.onWayPointDelete(callback);
	}

	public getMany(ids: number[]) {
		return this.api.getMany(ids);
	}

	public get(id: number) {
		return this.api.get(id);
	}

	public getAll(): Task[] {
		return Object.values(this.api.getAllById());
	}

	public getOpen(): Task[] {
		return this.api.getGroup(TaskGroupTypes.Main, GROUP_VALUES.Open);
	}

	public getPlanning(): Task[] {
		return this.api.getGroup(TaskGroupTypes.Main, GROUP_VALUES.Planning);
	}

	public async loadOpen(): Promise<Task[]> {
		const response = await this.api.loadOpen(null);
		return response.items;
	}

	public async loadPlanning(additionalColumns?: string[]): Promise<Task[]> {
		const taskFields = additionalColumns?.length
			? [...DEFAULT_PLANNED_TASK_FIELDS, ...additionalColumns]
			: DEFAULT_PLANNED_TASK_FIELDS;

		const response = await this.api.loadPlanning(taskFields);
		return response.items;
	}

	public async load(taskId: number, force?: boolean): Promise<Task> {
		return await this.api.load(taskId, { useCache: !force });
	}

	public async loadMany(taskIds: number[], fields?: ItemFields, options?: DataOptions): Promise<Task[]> {
		if (!taskIds?.length) {
			return [];
		}
		return await this.api.loadMany(taskIds, fields, options);
	}

	public async update(taskId: number, diff: Partial<Task>): Promise<void> {
		await this.api.update({ ...diff, id: taskId });
	}

	public async duplicate(taskId: number): Promise<number> {
		const duplicatedTask = await this.api.duplicate(taskId);
		return duplicatedTask.id;
	}

	public async addTaskNote({
		taskId,
		wayPointId,
		note,
		hiddenFromDriver
	}: {
		taskId: number;
		wayPointId: number;
		note: string;
		hiddenFromDriver?: boolean;
	}) {
		return this.api.addTaskNote(taskId, wayPointId, note, undefined, undefined, hiddenFromDriver);
	}

	public async moveToPlanning(ids: number[]) {
		const response = { success: false };

		const v2response = await this.api.moveToPlanning(ids);
		response.success = v2response.success;

		return response;
	}

	public async releaseToDrivers(ids: number[]) {
		const response = { success: false };

		const v2response = await this.api.releaseToDrivers(ids);
		response.success = v2response.success;

		return response;
	}

	public massCancel(
		taskIds: number[],
		reasonId: number,
		text?: string,
		automaticallyCancelled?: TaskAutomaticallyCancelled
	): Promise<boolean> {
		return this.api.massCancel(taskIds, reasonId, text, automaticallyCancelled);
	}

	public async massAssign(
		userId: number,
		taskIds: number[],
		options = {},
		targetRun?: Partial<Run>
	): Promise<TaskGeneralResponse> {
		return this.api.massAssign(userId, taskIds, options, targetRun);
	}

	public async massUnassign(taskIds: number[], options = {}): Promise<TaskGeneralResponse> {
		return this.api.massUnassign(taskIds, options);
	}
}
